<template>
  <UPopover
    v-model:open="panelOpen"
    :content="{ side: 'bottom', align: 'end', sideOffset: 8 }"
    :ui="{ content: 'z-[120] bg-[var(--fb-surface)]' }"
  >
    <button class="user-trigger" type="button">
      <span class="user-avatar">{{ userInitial }}</span>
      <span class="user-name">{{ displayName }}</span>
      <UIcon class="user-chevron" name="i-lucide-chevron-down" />
    </button>

    <template #content="{ close }">
      <div class="user-panel">
        <p class="user-greeting">{{ t('account.greeting', { name: displayName }) }}</p>

        <div class="user-panel-nav">
          <UButton
            color="neutral"
            variant="ghost"
            icon="i-lucide-lock-keyhole"
            size="sm"
            block
            @click="goChangePassword(close)"
          >
            {{ t('account.changePassword') }}
          </UButton>
          <UButton
            color="neutral"
            variant="ghost"
            icon="i-lucide-ticket-percent"
            size="sm"
            block
            @click="goBindReferrer(close)"
          >
            {{ t('account.bindReferrer') }}
          </UButton>
        </div>

        <div class="referral-section">
          <h3 class="referral-title">{{ t('account.myReferral') }}</h3>
          <div class="referral-card">
            <div
              class="referral-row"
              style="padding-bottom: 13px; border-bottom: 1px solid #fcfcfc"
            >
              <span class="referral-label">{{ t('account.referralCode') }}</span>
              <strong class="referral-value">{{ referralCode || '—' }}</strong>
              <UButton
                color="neutral"
                variant="ghost"
                size="xs"
                :disabled="!referralCode"
                class="referral-pill cursor-pointer"
                @click="copyCode"
              >
                {{ t('account.copy') }}
              </UButton>
            </div>
            <div class="referral-row" style="justify-content: space-between">
              <div style="display: flex; align-items: baseline">
                <span class="referral-label">{{ t('account.referralCount') }}</span>
                <strong class="referral-value" style="margin-left: 10px; font-size: 28px">{{
                  referralCount
                }}</strong>
              </div>
              <UButton
                color="neutral"
                variant="ghost"
                size="xs"
                class="referral-pill cursor-pointer"
                @click="viewDetail(close)"
              >
                {{ t('account.viewDetail') }}
              </UButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </UPopover>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useToast } from '@nuxt/ui/composables'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { getReferrerInfo } from '@/api/user'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()

const displayName = computed(() => authStore.displayName || 'FindBao')
const userInitial = computed(() => (displayName.value || 'F').slice(0, 1).toUpperCase())

const panelOpen = ref(false)
const referralCode = ref('')
const referralCount = ref(0)

async function loadReferral() {
  try {
    const response = await getReferrerInfo()
    referralCode.value = String(response.data?.referrer_code || '')
    referralCount.value = Math.max(0, Number(response.data?.people_count) || 0)
  } catch {
    /* 接口异常时保留空占位，不阻塞弹窗 */
  }
}

watch(panelOpen, (open) => {
  if (open) void loadReferral()
})

const notify = (title: string, color: 'success' | 'info' = 'success') =>
  toast.add({
    title,
    color: color === 'success' ? 'success' : 'info',
    icon: color === 'success' ? 'i-lucide-circle-check' : 'i-lucide-info',
    duration: 3000,
  })

function goChangePassword(close?: () => void) {
  close?.()
  void router.push({ name: 'change-password', params: { locale: route.params.locale } })
}

function goBindReferrer(close?: () => void) {
  close?.()
  void router.push({ name: 'reffer-bind', params: { locale: route.params.locale } })
}

async function copyCode() {
  if (!referralCode.value) return
  try {
    await navigator.clipboard.writeText(referralCode.value)
    notify(t('account.copied'))
  } catch {
    /* Clipboard unavailable, nothing else to do. */
  }
}

function viewDetail(close?: () => void) {
  close?.()
  void router.push({ name: 'referrer-list', params: { locale: route.params.locale } })
}
</script>

<style scoped>
.user-trigger {
  height: 42px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: transparent;
  color: var(--fb-ink);
  cursor: pointer;
  transition:
    border-color 0.18s,
    background 0.18s;
}
.user-trigger:hover {
  border-color: var(--fb-line);
  background: color-mix(in srgb, var(--fb-surface) 92%, transparent);
}
.user-avatar {
  flex: none;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--fb-coral-soft);
  color: var(--fb-coral-dark);
  font-size: 13px;
  font-weight: 850;
}
.user-name {
  max-width: 150px;
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-chevron {
  flex: none;
  color: var(--fb-ink-soft);
  font-size: 14px;
}
.user-panel {
  width: 290px;
  max-width: calc(100vw - 24px);
  padding: 14px;
  display: grid;
  gap: 10px;
}
.user-greeting {
  margin: 0;
  padding: 0 4px;
  overflow: hidden;
  color: var(--fb-ink);
  font-size: 14px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-panel-nav {
  display: grid;
  gap: 2px;
}
.user-panel-nav :deep(button) {
  justify-content: flex-start;
}
.referral-section {
  display: grid;
  gap: 8px;
}
.referral-title {
  margin: 0;
  padding: 0 4px;
  color: var(--fb-ink);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}
.referral-card {
  padding: 14px;
  display: grid;
  gap: 10px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0697ff 0%, #4c72ff 100%);
  color: #fff;
}
.referral-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.referral-label {
  flex: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  line-height: 1.4;
}
.referral-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.referral-pill {
  flex: none;
  height: 22px;
  padding: 0 9px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
}
.referral-pill:hover {
  background: rgba(255, 255, 255, 0.26);
  color: #fff;
}
.referral-pill:disabled {
  border-color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  opacity: 1;
}
@media (max-width: 900px) {
  .user-name {
    display: none;
  }
}
</style>
